import { find, forEach } from "ramda";

import { getById } from "helpers/collections";
import applyStockLevel from "packages/online_ordering/api/helpers/stock_level_transform";
import {
  addSelectedModInModGroupToItem,
  createCartItem,
} from "packages/online_ordering/helpers/cart";
import {
  allMinimumsMet,
  getMaxAllowedQty,
} from "packages/online_ordering/helpers/item_modal";

/** @typedef {{id: string | number}} SerializedSide */
/** @typedef {{id: string | number, modifierGroupId: string | number}} SerializedModifier */
/** @typedef {{itemId: string | number, quantity: number, sides: SerializedSide[], modifiers: SerializedModifier[]}} SerializedReorderItem */
/** @typedef {import("types/menu").IItem & {modifierGroups: Array<{id: string | number, minimumRequired: number, modifiers: Array<{id: string | number, modifierGroupId: string | number}>}>}} ReorderMenuItem */

const getSectionByItemId = (id, menu) => {
  return find(
    /** @param {{items: ReorderMenuItem[]}} section */
    section => Boolean(getById(id, section.items)),
    menu
  );
};

/** @param {string | number} id @param {Array<{items: ReorderMenuItem[]}>} menu */
const getItemFromSection = (id, menu) => {
  const section = getSectionByItemId(id, menu);
  return section ? getById(id, section.items) : null;
};

/** @param {Array<{items: ReorderMenuItem[]}>} menu @param {SerializedReorderItem} item */
const findAndExtendItemFromMenu = (menu, item) => {
  const foundItem = getItemFromSection(item.itemId, menu);
  return foundItem
    ? { ...createCartItem(foundItem), quantity: item.quantity }
    : null;
};

/** @param {string | number} id @param {ReorderMenuItem} item */
const getModGroupById = (id, item) => {
  return find(
    modGroup => Boolean(getById(id, modGroup.modifiers)),
    item.modifierGroups
  );
};

/** @param {string | number} id @param {ReorderMenuItem} foundItem */
const getModFromItem = (id, foundItem) => {
  const modGroup = getModGroupById(id, foundItem);
  return modGroup ? getById(id, modGroup.modifiers) : null;
};

function recreateItemWithSidesOrReject(item, foundItem) {
  /** @type {SerializedReorderItem} */
  const typedItem = item;
  let allSidesValid = true;
  let itemWithSides = foundItem;
  forEach(side => {
    const foundSide = getById(side.id, foundItem.sides);
    if (foundSide) {
      itemWithSides = {
        ...foundItem,
        selectedSides: [...foundItem.selectedSides, foundSide],
      };
    } else {
      allSidesValid = false;
    }
  }, typedItem.sides);
  return allSidesValid ? itemWithSides : null;
}

function recreateItemWithModsOrReject(item, foundItem) {
  /** @type {SerializedReorderItem} */
  const typedItem = item;
  let allModsValid = true;
  let itemWithMods = foundItem;

  forEach(modifier => {
    const itemModifier = getModFromItem(modifier.id, foundItem);
    if (itemModifier) {
      const selectedModsGroup = getById(
        modifier.modifierGroupId,
        itemWithMods.selectedModifiers
      );
      itemWithMods = addSelectedModInModGroupToItem(
        selectedModsGroup,
        itemWithMods,
        itemModifier
      );
    } else {
      allModsValid = false;
    }
  }, typedItem.modifiers);
  return allModsValid ? itemWithMods : null;
}

function recreateItemOrReject(menu, itemToRecreate, stockLevels, cartItems) {
  const foundItem = findAndExtendItemFromMenu(menu, itemToRecreate);
  if (foundItem) {
    const withStockLevel = applyStockLevel(foundItem, cartItems, stockLevels);

    const foundItemWithSides = recreateItemWithSidesOrReject(
      itemToRecreate,
      withStockLevel
    );
    if (foundItemWithSides) {
      const foundItemWithSidesAndMods = recreateItemWithModsOrReject(
        itemToRecreate,
        foundItemWithSides
      );
      if (foundItemWithSidesAndMods) {
        const maxQuantity = getMaxAllowedQty(foundItemWithSidesAndMods);

        return allMinimumsMet(foundItemWithSidesAndMods) &&
          maxQuantity >= foundItemWithSidesAndMods.quantity
          ? foundItemWithSidesAndMods
          : null;
      }
    }
  }

  return null;
}

function filterReorderIntoValidAndInvalid(
  serializedReorder,
  menu,
  stockLevels
) {
  let validItems = [];
  let invalidItems = [];

  forEach(item => {
    const validatedItem = recreateItemOrReject(
      menu,
      item,
      stockLevels,
      validItems
    );

    if (validatedItem) {
      validItems = [...validItems, validatedItem];
    } else {
      invalidItems = [...invalidItems, item];
    }
  }, JSON.parse(serializedReorder));
  return { invalidItems, validItems };
}

export { filterReorderIntoValidAndInvalid };
